/**
 * useActivitySeenTracker
 *
 * Marks activity items seen on *visibility with dwell*, not on drawer open and
 * not on click:
 *
 *   An item is seen once it has been >=50% visible in the drawer's scroll
 *   container for 1s continuously, while the browser tab itself is visible.
 *
 * That rule is what survives the cases a simpler one gets wrong. Marking on open
 * would clear 40 unread the instant the drawer appears. Marking on click would
 * strand every item the user actually read in the preview. Marking on any
 * intersection would clear everything the user flicked past while scrolling. The
 * document-visibility guard stops a drawer left open on a second monitor from
 * silently draining the badge.
 *
 * Newly-seen ids are buffered and flushed on a debounce, on unmount (drawer
 * close or tab change), and on `visibilitychange`, so a burst of scrolling costs
 * one request rather than one per row.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useTaskPortalActivityFeedService } from '@actschurch/ccms-sdk';
import { activityKey, useTaskActivityStore } from '@/stores/taskActivityStore';

/** Continuous visible time before an item counts as read. */
const DWELL_MS = 1000;

/** Quiet period before the buffer is sent. */
const FLUSH_DEBOUNCE_MS = 2000;

const VISIBILITY_THRESHOLD = 0.5;

export interface SeenCandidate {
  activityTypeId: number;
  itemId: number;
  taskId: number;
}

export function useActivitySeenTracker(scrollRootRef: React.RefObject<HTMLElement>) {
  const feedService = useTaskPortalActivityFeedService();
  const markSeenLocal = useTaskActivityStore((s) => s.markSeenLocal);

  const observerRef = useRef<IntersectionObserver | null>(null);
  const dwellTimersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const bufferRef = useRef(new Map<string, SeenCandidate>());
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const candidatesRef = useRef(new Map<Element, SeenCandidate>());

  const flush = useCallback(async () => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }

    const pending = Array.from(bufferRef.current.values());
    if (pending.length === 0) return;
    bufferRef.current.clear();

    try {
      await feedService.markSeen(
        pending.map((c) => ({
          activityTypeID: c.activityTypeId,
          itemID: c.itemId,
          taskID: c.taskId,
        }))
      );
    } catch {
      // The receipt is idempotent, so re-queue and let the next flush retry
      // rather than losing the read state entirely.
      pending.forEach((c) => bufferRef.current.set(activityKey(c.activityTypeId, c.itemId), c));
    }
  }, [feedService]);

  const scheduleFlush = useCallback(() => {
    if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    flushTimerRef.current = setTimeout(() => void flush(), FLUSH_DEBOUNCE_MS);
  }, [flush]);

  const enqueue = useCallback(
    (candidate: SeenCandidate) => {
      const key = activityKey(candidate.activityTypeId, candidate.itemId);
      if (bufferRef.current.has(key)) return;

      bufferRef.current.set(key, candidate);
      // Optimistic: the badge should react as the user reads, not a flush later.
      markSeenLocal([key]);
      scheduleFlush();
    },
    [markSeenLocal, scheduleFlush]
  );

  // One observer for the whole list, rebuilt only when the scroll root changes.
  useEffect(() => {
    const root = scrollRootRef.current;
    if (!root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const candidate = candidatesRef.current.get(entry.target);
          if (!candidate) return;

          const key = activityKey(candidate.activityTypeId, candidate.itemId);
          const timers = dwellTimersRef.current;

          const visible =
            entry.isIntersecting &&
            entry.intersectionRatio >= VISIBILITY_THRESHOLD &&
            document.visibilityState === 'visible';

          if (visible) {
            if (timers.has(key)) return;
            timers.set(
              key,
              setTimeout(() => {
                timers.delete(key);
                enqueue(candidate);
              }, DWELL_MS)
            );
          } else {
            // Scrolled away before the dwell elapsed — it was never read.
            const timer = timers.get(key);
            if (timer) {
              clearTimeout(timer);
              timers.delete(key);
            }
          }
        });
      },
      { root, threshold: [VISIBILITY_THRESHOLD] }
    );

    observerRef.current = observer;

    return () => {
      observer.disconnect();
      observerRef.current = null;
      dwellTimersRef.current.forEach((t) => clearTimeout(t));
      dwellTimersRef.current.clear();
    };
  }, [scrollRootRef, enqueue]);

  // Backgrounding the tab must stop the dwell timers, or an unattended drawer
  // marks everything on screen as read.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        dwellTimersRef.current.forEach((t) => clearTimeout(t));
        dwellTimersRef.current.clear();
        void flush();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [flush]);

  // Flush whatever is buffered when the drawer closes or the tab changes.
  useEffect(() => () => void flush(), [flush]);

  /**
   * Ref callback for an item row. Attach to each rendered item so the observer
   * can track it: `<div ref={registerItem(candidate)} />`.
   */
  const registerItem = useCallback(
    (candidate: SeenCandidate) => (node: HTMLElement | null) => {
      const observer = observerRef.current;
      if (!observer) return;

      if (node) {
        candidatesRef.current.set(node, candidate);
        observer.observe(node);
      }
    },
    []
  );

  return { registerItem, flush };
}
